export type LeadCategoryColorId =
  | 'orange'
  | 'blue'
  | 'cyan'
  | 'emerald'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'indigo';

export type LeadCategoryColorOption = {
  id: LeadCategoryColorId;
  label: string;
  text: string;
  swatch: string;
  /** Soft pill: heller Hintergrund + Text, für Tags auf Karten. */
  pill: string;
};

/** User-selectable colors — no red/rose. */
export const LEAD_CATEGORY_COLOR_OPTIONS: LeadCategoryColorOption[] = [
  { id: 'orange', label: 'Orange', text: 'text-orange-600 dark:text-orange-300', swatch: 'bg-orange-500', pill: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-800' },
  { id: 'blue', label: 'Blau', text: 'text-blue-600 dark:text-blue-300', swatch: 'bg-blue-500', pill: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-800' },
  { id: 'cyan', label: 'Cyan', text: 'text-cyan-600 dark:text-cyan-300', swatch: 'bg-cyan-500', pill: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-200 dark:ring-cyan-800' },
  { id: 'emerald', label: 'Grün', text: 'text-emerald-600 dark:text-emerald-300', swatch: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-800' },
  { id: 'violet', label: 'Violett', text: 'text-violet-600 dark:text-violet-300', swatch: 'bg-violet-500', pill: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800' },
  { id: 'amber', label: 'Gelb', text: 'text-amber-600 dark:text-amber-300', swatch: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800' },
  { id: 'teal', label: 'Petrol', text: 'text-teal-600 dark:text-teal-300', swatch: 'bg-teal-500', pill: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-200 dark:ring-teal-800' },
  { id: 'indigo', label: 'Indigo', text: 'text-indigo-600 dark:text-indigo-300', swatch: 'bg-indigo-500', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-200 dark:ring-indigo-800' },
];

export const LEAD_CATEGORY_COLOR_IDS = LEAD_CATEGORY_COLOR_OPTIONS.map((o) => o.id);

const COLOR_TEXT_BY_ID = Object.fromEntries(
  LEAD_CATEGORY_COLOR_OPTIONS.map((o) => [o.id, o.text]),
) as Record<LeadCategoryColorId, string>;

const DEFAULT_COLOR_BY_KEY: Record<string, LeadCategoryColorId> = {
  heizung: 'orange',
  sanitaer: 'blue',
  klima: 'cyan',
};

export function normalizeLeadCategoryColor(
  raw: unknown,
  sortOrder = 0,
): LeadCategoryColorId {
  const s = raw != null ? String(raw).trim().toLowerCase() : '';
  if (s && (LEAD_CATEGORY_COLOR_IDS as readonly string[]).includes(s)) {
    return s as LeadCategoryColorId;
  }
  return LEAD_CATEGORY_COLOR_IDS[sortOrder % LEAD_CATEGORY_COLOR_IDS.length] ?? 'orange';
}

export function defaultLeadCategoryColorForKey(key: string): LeadCategoryColorId {
  const k = key.trim().toLowerCase();
  if (DEFAULT_COLOR_BY_KEY[k]) return DEFAULT_COLOR_BY_KEY[k];
  return 'orange';
}

export function defaultLeadCategoryColorForIndex(index: number): LeadCategoryColorId {
  return LEAD_CATEGORY_COLOR_IDS[index % LEAD_CATEGORY_COLOR_IDS.length] ?? 'orange';
}

/** Bold colored text class from stored color slug. */
export function leadCategoryTextClassName(color: string | null | undefined): string {
  const id = normalizeLeadCategoryColor(color, 0);
  return COLOR_TEXT_BY_ID[id] ?? 'text-slate-400';
}

export function leadCategorySwatchClassName(color: string | null | undefined): string {
  const id = normalizeLeadCategoryColor(color, 0);
  return LEAD_CATEGORY_COLOR_OPTIONS.find((o) => o.id === id)?.swatch ?? 'bg-slate-400';
}

export function leadCategoryPillClassName(color: string | null | undefined): string {
  const id = normalizeLeadCategoryColor(color, 0);
  return LEAD_CATEGORY_COLOR_OPTIONS.find((o) => o.id === id)?.pill ?? 'bg-app-surface-muted text-app-text-muted ring-app-border';
}
